import React from 'react'
import { DragDropContext, Droppable, Draggable, DropResult, DraggableProvided } from 'react-beautiful-dnd'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Icon, IconButton, Tooltip, TooltipTrigger, TooltipContent } from '@shopflo/ui'
import { CART_SCROLLABLE_ELEMENTS, CART_SCROLLABLE_ELEMENTS_TYPE } from './config'
import { DotsSixVertical, Eye, EyeSlash, Warning } from '@phosphor-icons/react'
import { useCartContext } from 'src/lib/contexts/CartContext'
import { classNames } from 'src/lib/utils/helpers'
import { CartElementType, useCartLayoutContext } from 'src/lib/contexts/CartLayoutContext'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { useCheckoutUI } from 'src/lib/contexts/CheckoutUIContext'
import { useCartConfigItemHighlight } from './useCartConfigItemHighlight'
import { useLocale } from 'src/lib/hooks/useLocal'

interface DraggableScrollableItemProps {
    element: typeof CART_SCROLLABLE_ELEMENTS[CART_SCROLLABLE_ELEMENTS_TYPE]
    provided: DraggableProvided
    visible?: boolean
    onToggleVisibility?: (value: boolean) => void
    isDeactivated?: boolean
    selectedElement: CartElementType | null
    selectedFromPreview: boolean
    setSelectedElement: (payload: CartElementType | null, options?: { fromPreview?: boolean }) => void
}

const DraggableScrollableItem: React.FC<DraggableScrollableItemProps> = ({
    element,
    provided,
    visible,
    onToggleVisibility,
    isDeactivated,
    selectedElement,
    selectedFromPreview,
    setSelectedElement
}) => {
    const { t } = useLocale()
    const Component = element.component
    const isSelected = selectedElement === element.type
    const {
        itemRef,
        highlightRef,
        isOpen,
        handleValueChange,
        highlightOverlayClassName
    } = useCartConfigItemHighlight(
        element.type as CartElementType,
        isSelected,
        selectedFromPreview,
        selectedElement,
        setSelectedElement
    )

    return (
        <div ref={itemRef} className="relative rounded-lg">
            {isSelected && (
                <div ref={highlightRef} className={highlightOverlayClassName} aria-hidden />
            )}
            <Accordion
                key={element.type}
                type='single'
                collapsible
                value={isOpen ? element.type : ''}
                onValueChange={handleValueChange}
                className='bg-zinc-50 rounded-lg w-full px-3 py-2 hover:bg-zinc-950/6'
            >
                <AccordionItem value={element.type}>
                    <AccordionTrigger className='py-0 flex items-center'>
                        <div className='flex items-center justify-between w-full'>
                            <div className='flex items-center w-full gap-2 min-h-7'>
                                <div {...provided.dragHandleProps}>
                                    <Icon variant='ghost'
                                        size='xs'
                                        IconComponent={DotsSixVertical}
                                        iconProps={{ weight: 'bold' }}
                                        className='text-zinc-500'
                                    />
                                </div>
                                <p className={classNames('text-text-sm-medium text-zinc-800', visible === false ? 'text-zinc-800/60' : 'text-zinc-800/100')}>{element.title}</p>
                            </div>
                            {isDeactivated ? (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div
                                            className='mr-1 flex w-[20px] items-center justify-center'
                                            onClick={e => e.stopPropagation()}
                                        >
                                            <Icon
                                                IconComponent={Warning}
                                                size='xs'
                                                className='text-yellow-500'
                                                iconProps={{ weight: 'fill' }}
                                            />
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent side='top'>
                                        {t('upsell_inactive_in_dashboard')}
                                    </TooltipContent>
                                </Tooltip>
                            ) : onToggleVisibility && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <IconButton
                                            className='mr-1'
                                            variant='ghost'
                                            size='xs'
                                            IconComponent={visible ? Eye : EyeSlash}
                                            iconProps={{ weight: 'regular' }}
                                            onClick={e => {
                                                e.stopPropagation()
                                                onToggleVisibility?.(!visible)
                                            }}
                                        />
                                    </TooltipTrigger>
                                    <TooltipContent side='top'>
                                        Show/Hide Element
                                    </TooltipContent>
                                </Tooltip>
                            )}
                        </div>
                    </AccordionTrigger>
                    <AccordionContent>
                        <div className='pt-4 px-2'>
                            <Component />
                        </div>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </div>
    )
}

export const CartScrollableElements = () => {
    const {
        getters: { cartUIData },
        actions: { setCartUIConfigData, setCartCouponVisibilityConfig }
    } = useCartContext()

    const {
        getters: { selectedElement, selectedFromPreview },
        actions: { setSelectedElement }
    } = useCartLayoutContext()

    const {
        getters: { merchant },
    } = useMerchant()

    const {
        getters: { checkoutUI },
    } = useCheckoutUI()

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return

        const items = Array.from(cartUIData.elementOrder)
        const [reorderedItem] = items.splice(result.source.index, 1)
        items.splice(result.destination.index, 0, reorderedItem)

        setCartUIConfigData({ elementOrder: items }, 'ELEMENT_ORDER')
    }

    const isDiscountsVisible = cartUIData.couponVisibilityConfig.find(config => {
        return config.type === 'CART' && config.areCouponsVisible
    })?.areCouponsVisible ?? false

    const isNotesVisible = merchant?.attributes?.cartNote.enabled ?? false

    /**
     * Determines if an element should be shown in the UI
     * This function centralizes all visibility logic for scalability
     */
    const shouldShowElement = (type: CART_SCROLLABLE_ELEMENTS_TYPE): boolean => {
        switch (type) {
            case 'NOTES':
                return isNotesVisible
            default:
                return true
        }
    }

    const featureEnabledMap: Partial<Record<CART_SCROLLABLE_ELEMENTS_TYPE, { value: boolean; setValue: (value: boolean) => void }>> = {
        DISCOUNTS: {
            value: isDiscountsVisible,
            setValue: (value: boolean) => {
                const updatedConfig = cartUIData.couponVisibilityConfig.map(config => {
                    if (config.type === 'CART') {
                        return {
                            ...config,
                            areCouponsVisible: value
                        }
                    }
                    return config
                })
                setCartCouponVisibilityConfig(updatedConfig)
            }
        },
        UPSELL: {
            value: cartUIData.upsell.isEnabled,
            setValue: (value: boolean) => {
                setCartUIConfigData({ ...cartUIData.upsell, isEnabled: value }, 'UPSELL')
            }
        }
    }


    return (
        <div>
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId='scrollable-elements-list'>
                    {(provided) => (
                        <div className='space-y-2' ref={provided.innerRef} {...provided.droppableProps}>
                            {cartUIData.elementOrder
                                .filter(type => shouldShowElement(type as CART_SCROLLABLE_ELEMENTS_TYPE))
                                .map((type, index) => {
                                    const element = CART_SCROLLABLE_ELEMENTS[type as CART_SCROLLABLE_ELEMENTS_TYPE]
                                    return (
                                        <Draggable
                                            draggableId={type}
                                            index={index}
                                            key={type}
                                        >
                                            {(provided) => (
                                                <div ref={provided.innerRef} {...provided.draggableProps}>
                                                    <DraggableScrollableItem
                                                        element={element}
                                                        provided={provided}
                                                        visible={featureEnabledMap[type]?.value}
                                                        onToggleVisibility={featureEnabledMap[type]?.setValue}
                                                        isDeactivated={type === 'UPSELL' && !checkoutUI?.upSellConfig?.isEnabled}
                                                        selectedElement={selectedElement}
                                                        selectedFromPreview={selectedFromPreview}
                                                        setSelectedElement={setSelectedElement}
                                                    />
                                                </div>
                                            )}
                                        </Draggable>
                                    )
                                })}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    )
}
